import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manage horizontal scroll container visibility and helpers for scroll arrows
 */
export const useScrollButtons = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const updateVisibility = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    updateVisibility();
    el.addEventListener('scroll', updateVisibility);
    window.addEventListener('resize', updateVisibility);

    return () => {
      el.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [updateVisibility]);

  const scrollToEdge = (direction: 'left' | 'right') => {
    const el = containerRef.current;
    if (!el) return;

    const target = direction === 'left' ? 0 : el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  return { containerRef, showLeft, showRight, scrollToEdge } as const;
};
